import { mergeRoleUis } from "../access/RoleUiCatalogue";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AuthContext } from "./AuthContext";
import { authStorage } from "./AuthStorage";
import { jwtHelper } from "./JwtHelper";
import { authService } from "../services/AuthService";
import { AuthEventName } from "../constants/AuthEventName";
import type { IAuthContext } from "./IAuthContext";
import type { LoggedInUser } from "../models/LoggedInUser";
import type { LoginRequest } from "../models/LoginRequest";
import type { LoginResponse } from "../models/LoginResponse";

interface AuthProviderProps {
  children: ReactNode;
}

const initialState: LoggedInUser = {
  User: undefined,
  Token: undefined,
  RoleCodes: [],
  Permissions: [],
  IsAuthenticated: false,
  IsInitialized: false,
};

export default function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<LoggedInUser>(initialState);

  const clearAuthentication = useCallback((): void => {
    authStorage.clearSession();
    setState({ ...initialState, IsInitialized: true });
  }, []);

  const initialize = useCallback((): void => {
    const session = authStorage.getSession();

    if (!session || !jwtHelper.isValid(session.Token)) {
      clearAuthentication();
      return;
    }

    setState({
      User: session.User,
      Token: session.Token,
      RoleCodes: session.RoleCodes ?? [],
      Permissions: session.Permissions ?? [],
      RoleUis: session.RoleUis ?? undefined,
      IsAuthenticated: true,
      IsInitialized: true,
    });
  }, [clearAuthentication]);

  const login = useCallback(
    async (request: LoginRequest): Promise<LoginResponse> => {
      const response = await authService.login(request);

      if (!response.Token || !jwtHelper.isValid(response.Token)) {
        throw new Error("The server returned an invalid authentication token.");
      }

      const roles=response.Roles ?? response.User.Roles;
      const suppliedUis=response.RoleUis ?? (roles ? roles.filter(r=>r.IsActive).flatMap(r=>r.RoleUis??[]) : undefined);
      const session: LoginResponse = {
        Token: response.Token,
        User: response.User,
        RoleCodes: response.RoleCodes?.length ? response.RoleCodes : response.User.RoleCodes ?? roles?.map(r=>r.Code) ?? [],
        Permissions: response.Permissions ?? [],
        RoleUis:
          suppliedUis == null ? undefined : mergeRoleUis(suppliedUis),
      };

      authStorage.setSession(session);
      setState({
        User: session.User,
        Token: session.Token,
        RoleCodes: session.RoleCodes,
        Permissions: session.Permissions,
        RoleUis: session.RoleUis ?? undefined,
        IsAuthenticated: true,
        IsInitialized: true,
      });

      return session;
    },
    [],
  );

  const logout = useCallback((): void => {
    clearAuthentication();
  }, [clearAuthentication]);

  const hasRole = useCallback(
    (roleCode: string): boolean =>
      state.User?.IsTenantAdmin === true ||
      state.RoleCodes.some(
        (existingRoleCode) =>
          existingRoleCode.localeCompare(roleCode, undefined, {
            sensitivity: "accent",
          }) === 0,
      ),
    [state.RoleCodes, state.User?.IsTenantAdmin],
  );

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (state.User?.IsTenantAdmin) return true;
      if (state.RoleUis !== undefined) {
        const [page, action] = permission.split(".");
        const ui = state.RoleUis.find((x) => x.Code === page);
        if (!ui) return false;
        return (
          action === "View" ||
          (action === "Manage"
            ? ui.Actions.length > 0
            : ui.Actions.includes(action))
        );
      }
      return state.Permissions.some(
        (p) => p.toLowerCase() === permission.toLowerCase(),
      );
    },
    [state.Permissions, state.RoleUis, state.User?.IsTenantAdmin],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => permissions.some(hasPermission),
    [hasPermission],
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => permissions.every(hasPermission),
    [hasPermission],
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const handleUnauthorized = (): void => {
      clearAuthentication();
    };

    window.addEventListener(AuthEventName.Unauthorized, handleUnauthorized);
    return () => {
      window.removeEventListener(
        AuthEventName.Unauthorized,
        handleUnauthorized,
      );
    };
  }, [clearAuthentication]);

  useEffect(() => {
    if (!state.Token) return;
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      clearTimeout(timer);
      if (!state.Token || jwtHelper.isExpired(state.Token)) { clearAuthentication(); return; }
      const expiresAt = (jwtHelper.decode(state.Token)?.exp ?? 0) * 1000;
      timer = setTimeout(check, Math.min(Math.max(expiresAt - Date.now(), 1), 2147483647));
    };
    check();
    window.addEventListener("focus", check);
    return () => { clearTimeout(timer); window.removeEventListener("focus", check); };
  }, [state.Token, clearAuthentication]);

  const value = useMemo<IAuthContext>(
    () => ({
      state,
      login,
      logout,
      hasRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    }),
    [
      state,
      login,
      logout,
      hasRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
